<!--
	@name app-events-list
	@description events list component
	@date 2024/08/30
	@license no license
	@copywrite Answers In Retirement Limited
-->

<template>
	<div :component="$options.name">
		<div class="mt-10 mb-10">
			<p class="text-body-1 mb-10 mb-md-4">
				Don't miss out on our award winning, industry leading events, brought to you through Air and Air Academy. You're guaranteed to leave feeling inspired and
				equipped with the practical tools and resources you need to support your clients. Book your place early to avoid disappointment, as places are often limited.
			</p>
			<p class="text-body-1 mb-0 d-none d-md-block">
				Make the most of our filters to find the events you're looking for, you can also view the events in
				<a @click="viewOption = 1">calendar</a> or <a @click="viewOption = 2">map</a> view.
			</p>
		</div>

		<v-toolbar flat color="rgba(255,255,255,0)" class="d-none d-md-block events--toolbar">
			<v-select v-model="category" :items="categories" label="Categories" dense hide-details solo outlined style="max-width: 350px" @change="onCategoryChange" />
			<v-spacer />
			<v-btn-toggle v-show="viewOption === 0" v-model="eventPeriod" mandatory dense @change="onEventPeriodChange">
				<v-tooltip bottom>
					<template #activator="{ on }">
						<v-btn v-on="on">
							Upcoming
						</v-btn>
					</template>
					<span>Upcoming events</span>
				</v-tooltip>
				<v-tooltip bottom>
					<template #activator="{ on }">
						<v-btn v-on="on">
							Past
						</v-btn>
					</template>
					<span>Historic events</span>
				</v-tooltip>
			</v-btn-toggle>
			<v-divider v-show="viewOption === 0" inset vertical class="mx-3" />
			<v-btn-toggle v-model="viewOption" mandatory dense @change="onViewOptionChange">
				<v-tooltip bottom>
					<template #activator="{ on }">
						<v-btn v-on="on">
							<v-icon>mdi-view-list-outline</v-icon>
						</v-btn>
					</template>
					<span>Grid view</span>
				</v-tooltip>
				<v-tooltip bottom>
					<template #activator="{ on }">
						<v-btn v-on="on">
							<v-icon>mdi-calendar</v-icon>
						</v-btn>
					</template>
					<span>Calendar view</span>
				</v-tooltip>
			</v-btn-toggle>
		</v-toolbar>
		<p v-if="!events.length" class="text-body-1 mt-3 mb-10">
			There are 0 {{ eventPeriod === 1 ? 'past' : 'upcoming' }} events for this category
		</p>
		<div v-if="viewOption === 0 && events.length">
			<v-row class="mt-1">
				<v-col v-for="(event, index) in events" :key="event.id" cols="12" sm="6" :md="index === 0 ? 8 : 4">
					<v-card hover style="position: relative" @click="createEventUrl(event)">
						<v-chip dark label color="rgba(23, 184, 144, 0.7)" class="pl-4 pr-8" style="position: absolute; top: 8px; left: -8px; z-index: 2">
							<v-avatar left rounded color="rgba(103, 0, 86, 0.3)">
								{{ $moment(event.start).format('DD') }}
							</v-avatar>
							<span class="text-body-1 font-weight-bold d-inline-block mr-1">
								{{ $moment(event.start).format('MMMM') }} </span>{{ $moment(event.start).format('YYYY') }}
						</v-chip>
						<v-chip v-if="event.category" small dark label color="rgba(103, 0, 86, 0.5)" style="position: absolute; top: 46px; left: -8px; z-index: 2">
							{{ event.category }}
						</v-chip>
						<app-events-event-countdown v-if="displayCountdown(event, index)" :event="event" />
						<v-img
							class="white--text align-end"
							:height="'368px'"
							:aspect-ratio="index === 0 ? '2/1' : '1/1'"
							:src="$options.filters.urlize(index === 0 ? event.image : (event.thumbnail || event.image)) || $options.filters.urlize('placeholder/gradient.png')"
						>
							<v-sheet color="rgba(0,0,0,0.75)">
								<v-card-title class="text-body-1 font-weight-bold white--text">
									{{ event.name | truncate(index === 0 && $vuetify.breakpoint.mdAndUp ? 100 : 40) }}
								</v-card-title>
								<v-card-subtitle class="grey--text text--lighten-2 pb-1">
									{{ event.description | truncate(index === 0 && $vuetify.breakpoint.mdAndUp ? 200 : 100) }}
								</v-card-subtitle>
								<v-card-subtitle class="white--text pt-0">
									<v-icon color="white" small style="position: relative; top: -1px; left: -2px">
										mdi-map-marker
									</v-icon>{{ event.address | truncate(index === 0 && $vuetify.breakpoint.mdAndUp ? 100 : 50) }}
								</v-card-subtitle>
							</v-sheet>
						</v-img>
					</v-card>
				</v-col>
			</v-row>
			<div class="text-center mt-3 mb-10">
				<v-btn color="primary" large :disabled="events.length >= totalEvents" @click="nextPage">
					{{ events.length >= totalEvents ? 'NO' : 'SEE' }} MORE EVENTS
				</v-btn>
			</div>
		</div>
		<div v-if="viewOption === 1" class="pt-3">
			<v-sheet elevation="3" color="white" class="mb-10">
				<common-structure-calendar :items="events" class="pt-1" @change="onCalendarChange" />
			</v-sheet>
		</div>
	</div>
</template>

<script>
	import { mapActions, mapGetters } from 'vuex';
	import CommonStructureCalendar from '@/component/common/structure/calendar';
	import AppEventsEventCountdown from '@/component/app/events/event-countdown';
	import { kebabcase } from 'lodash';

	export default {
		name: 'app-events-list',

		components: {
			CommonStructureCalendar,
			AppEventsEventCountdown
		},

		data() {
			return {
				category: 'All categories',
				categories: null,
				gridCategories: [],
				viewOption: 0,
				offset: 0,
				initialPageSize: 5,
				paginationPageSize: 6,
				eventPeriod: 0,
				year: null
			};
		},

		computed: {
			...mapGetters('CmsEvent', ['events', 'totalEvents']),

			displayCountdown() {
				return (event, index) => {
					if (index !== 0 || this.eventPeriod !== 0) return false;
					if (!event || !event.end) return false;
					if (this.$moment(event.end) < this.$moment()) return false;
					return true;
				};
			}
		},

		async created() {
			this.category = this.$route.query.category ?? this.category;
			const options = await this.getSchemaOptions('event-category');
			this.categories = (options || []).map(option => option.name);
			this.categories.unshift(this.category);

			if (!this.categories.includes(this.category)) {
				this.category = 'All categories';
				this.$router.push('/events');
			}

			this.loadGridEvents();
		},

		methods: {
			...mapActions('CmsEvent', ['loadEventsList']),
			...mapActions('Option', ['getSchemaOptions']),

			loadGridEvents() {
				this.offset = 0;
				this.loadEventsList({
					reset: true,
					category: this.category,
					period: this.eventPeriod,
					offset: this.offset,
					limit: this.initialPageSize
				});
			},

			onCategoryChange() {
				if (!this.category) return;
				if (this.$route.path === '/events') {
					const query = Object.assign({}, this.$route.query);
					delete query.category;
					this.$router.push({ path: 'events', query: { category: this.category, ...query } });
				}

				if (this.viewOption === 1) {
					this.onCalendarChange(this.year ?? new Date().getFullYear(), true);
					return;
				}

				this.loadGridEvents();
			},

			onEventPeriodChange() {
				if (this.viewOption !== 0) return;
				this.loadGridEvents();
			},

			onViewOptionChange() {
				if (this.viewOption === 1) {
					this.onCalendarChange(new Date().getFullYear(), true);
					return;
				}

				this.year = null;
				this.eventPeriod = 0;
				this.loadGridEvents();
			},

			createEventUrl(event) {
				if (this.$route.path === '/events') return this.$router.push(`/event/${kebabcase(event.name)}/${this.$moment(event.start).format('YYYY-MMMM-DD')}/${event.id}`);
				else this.$emit('select-event', event);
			},

            categoryImage(event) {
                let images = [
                    {category: 'Air Academy Summit', src: '/manager_documents/8955a55b-c4a6-440e-8670-ea1c4636da1e.jpg'},
                    {category: 'Air Academy Masterclass', src: '/manager_documents/58ae7004-b8a7-4f0c-b9e9-3b684075504e.jpg'},
                    {category: 'Air Conference', src: '/manager_documents/14177a70-5ecf-4517-9106-5fd828c8a9cb.jpg'},
                    {category: 'Air Awards', src: '/manager_documents/906ff07c-9e15-4e81-be99-89f6e1de67dd.jpg'},
                    {category: 'Training', src: '/manager_documents/906ff07c-9e15-4e81-be99-89f6e1de67dd.jpg'},
                    {category: 'Lending Criteria Clinic', src: '/manager_documents/8c5114be-e692-4d09-9a74-42bd99b044e9.jpg'},
                    {category: 'Ambassadors', src: '/manager_documents/cfc7163c-7960-4c82-b875-5881394c7d99.jpg'}
                ];

                let image = images.find((x) => x.category == event.category);
                if (image) return image.src;

                return '/news/6cf8663ffd75de6b0ed4be3fd50948f4.jpg';
            },

			nextPage() {
				this.offset = this.events.length;
				this.loadEventsList({
					category: this.category,
					period: this.eventPeriod,
					offset: this.offset,
					limit: this.paginationPageSize
				});
			},

			onCalendarChange(year, firstLoad = false) {
				if (!firstLoad && this.viewOption !== 1) return;
				if (!firstLoad && this.year !== null && this.year === year) return;
				this.year = year;
				this.loadEventsList({
					reset: true,
					category: this.category,
					period: this.eventPeriod,
					offset: 0,
					year: year
				});
			}
		}
	};
</script>

<style lang="scss" scoped>
	::v-deep .events--toolbar {
		.v-toolbar__content {
			padding: 0 !important;
		}
	}
</style>
